/**
 * The bell in the header.
 *
 * The badge polls; the list is only fetched while the panel is open, so a tab
 * left open all day costs one small count query a minute and nothing else.
 *
 * Opening a row marks that row read. "Mark all read" is deliberately separate
 * from opening the panel: seeing that something arrived is not the same as
 * having dealt with it, and clearing the badge on open would make the bell
 * useless to anyone who glances at it between meetings.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useMarkNotificationsRead, useNotifications, useUnreadCount } from '../api/hooks'
import type { AtlasNotification } from '../api/types'
import { NotificationList } from './NotificationList'
import { Button, ErrorNotice, Loading, cx } from './ui'

const PANEL_LIMIT = 20

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const unread = useUnreadCount()
  const list = useNotifications(PANEL_LIMIT)
  const markRead = useMarkNotificationsRead()

  const count = unread.data ?? 0

  // Escape closes, and so does a click anywhere else. Both listeners are only
  // attached while the panel is open.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    function onPointer(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  function handleOpenRow(notification: AtlasNotification) {
    setOpen(false)
    if (notification.readAt === null) markRead.mutate({ ids: [notification.id] })
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
      >
        <BellIcon />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white tabular-nums">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cx(
            // On a phone the bell sits close to the right edge, so a panel
            // anchored to the button hangs off the left of the screen and
            // clips its own text. Below `sm` it spans the viewport instead
            // and only hangs off the button once there is room for it.
            'fixed inset-x-3 top-14 z-40',
            'sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-2 sm:w-[22rem]',
            'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg',
            'dark:border-slate-800 dark:bg-slate-900',
          )}
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <p className="text-sm font-semibold">Notifications</p>
            {count > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markRead.mutate({ all: true })}
                loading={markRead.isPending}
              >
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {list.isLoading && <Loading />}
            {list.error && (
              <div className="p-3">
                <ErrorNotice error={list.error} />
              </div>
            )}
            {list.data && list.data.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                Nothing yet. Atlas tells you when work is assigned to you and when your meetings
                change.
              </p>
            )}
            {list.data && list.data.length > 0 && (
              <NotificationList notifications={list.data} onOpen={handleOpenRow} />
            )}
          </div>

          <div className="border-t border-slate-100 px-3 py-2 text-center dark:border-slate-800">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
            >
              See everything
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

function BellIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M10 2a5 5 0 0 0-5 5v2.6l-1.2 2.4A1 1 0 0 0 4.7 13.5h10.6a1 1 0 0 0 .9-1.5L15 9.6V7a5 5 0 0 0-5-5Zm0 15a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 10 17Z" />
    </svg>
  )
}
